import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useParams, useHistory } from "react-router";
import { Grid, Button, TextField, Typography, Box } from "@mui/material";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Loader from "../../components/Loader";
import {
  FormPageShell,
  FormPageCard,
  FormPageHeader,
  FormActions,
} from "../../components/ListPage";

import Swal from "sweetalert2";
import Toast from "../../utils/Toast";

import useEditCity from "../../mutation/city/editCity";
import useCreateAudit from "../../mutation/audit/createAudit";

import useGetCity from "../../queries/city/getCity";

import useProperties from "../../queries/property/getProperties";
import WordpressSuites from "../../components/roomTypes/WordpressSuites";

import { AuthContext } from "../../utils/userProvider";

const errorTextSx = {
  mx: 1.75,
  mt: 0.375,
  fontSize: "0.75rem",
  fontWeight: 400,
  color: "secondary.main",
};

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  name_chinese: yup.string().required("Name is required"),
  additionalSharerCost: yup
    .number()
    .typeError("Additional Sharer Cost must be a number")
    .required("Additional Sharer Cost is required")
    .positive("Additional Sharer Cost cannot be negative or 0"),
});

const EditCity = (props) => {
  const { id } = useParams();
  const history = useHistory();
  const { authState, setAuthState } = useContext(AuthContext);

  const [auditResponse, setAuditData] = useCreateAudit();

  const [currentData, setCurrentData] = useState();
  const [snapshotCurrent, setSnapshotCurrent] = useState({});
  const [cityResponse, resetCity] = useGetCity(id);

  const [propertiesTable, setPropertiesTable] = useState([]);

  const [propertiesResponse, resetProperties] = useProperties();

  const { control, register, handleSubmit, setValue, errors } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (propertiesResponse.data) {
      setPropertiesTable(
        propertiesResponse.data.map((p) => ({
          id: p.id,
          name: p.name,
          suiteIds: p.suiteIds,
        }))
      );
    }
  }, [propertiesResponse]);

  let [wordpressSuitesError, setWordpressSuitesError] = useState();

  const goBack = () => {
    window.location.href = "/dashboard/city";
  };

  const onSubmit = (values) => {
    /* let complete = true;
    if (selectedWordpressSuites.length < 1) {
      complete = false;
      setWordpressSuitesError("Please select a Suite(s)");
    }
    if (complete) { */
    let dataObj = {};
    let suites = {};
    //process form - use suite IDs from suiteIds.ids (numeric), not Strapi IDs
    let arraySuites = [];
    let propsWithoutSuiteIds = [];
    for (var i = 0; i < selectedWordpressSuites.length; i++) {
      let prop = selectedWordpressSuites[i];
      if (prop.suiteIds?.ids && Array.isArray(prop.suiteIds.ids)) {
        for (var j = 0; j < prop.suiteIds.ids.length; j++) {
          let sid = typeof prop.suiteIds.ids[j] === "number" ? prop.suiteIds.ids[j] : parseInt(prop.suiteIds.ids[j], 10);
          if (!isNaN(sid) && !arraySuites.includes(sid)) {
            arraySuites.push(sid);
          }
        }
      } else {
        propsWithoutSuiteIds.push(prop.name || prop.id);
      }
    }
    if (propsWithoutSuiteIds.length > 0) {
      setWordpressSuitesError(
        `The following properties have no Suite ID(s) configured in Strapi. Please add suite IDs (e.g. {"ids":[12682]}) to their SuiteIds field first: ${propsWithoutSuiteIds.join(", ")}`
      );
      return;
    }
    if (arraySuites.length === 0) {
      setWordpressSuitesError("No valid suite IDs found. Please ensure selected properties have SuiteIds.ids configured in Strapi.");
      return;
    }
    suites.ids = arraySuites;
    dataObj.suites = suites;

    dataObj.name = values.name;
    dataObj.name_chinese = values.name_chinese;
    dataObj.additionalSharerCost = values.additionalSharerCost;

    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          newData: dataObj,
          snapshotCurrent: snapshotCurrent,
          currentData: currentData,
          type: "city",
          action: "update",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
      }
    });
    /* } */
  };

  useEffect(() => {
    if (cityResponse.data && propertiesResponse.data) {
      setValue("name", cityResponse.data.name);
      setValue("name_chinese", cityResponse.data.name_chinese);
      setValue("additionalSharerCost", cityResponse.data.additionalSharerCost);
      setSnapshotCurrent(cityResponse.data);
      setCurrentData(cityResponse.data.id);
      if (cityResponse.data.suites && cityResponse.data.suites.ids) {
        let initialProperties = [];
        let suiteIds = cityResponse.data.suites.ids;

        for (var a = 0; a < suiteIds.length; a++) {
          let suiteId = typeof suiteIds[a] === "number" ? suiteIds[a] : parseInt(suiteIds[a], 10);
          let prop = propertiesResponse.data.find(
            (p) => p.suiteIds?.ids && Array.isArray(p.suiteIds.ids) && p.suiteIds.ids.includes(suiteId)
          );
          if (prop) {
            initialProperties.push({ id: prop.id, name: prop.name, suiteIds: prop.suiteIds });
          }
        }
        setSelectedWordpressSuites(initialProperties);
      }
    }
  }, [cityResponse, propertiesResponse]);

  useEffect(() => {
    try {
      if (auditResponse.data) {
        Toast.fire({
          icon: "success",
          title: "City Updation Audit Created",
        });
        history.push("/dashboard/city");
      } else if (auditResponse.error) {
        let data = auditResponse.errorMessage.response.data;
        if (data) {
          let messageData = data.message;
          let message = messageData.message;

          if (message == "Already Taken") {
            let itemTaken = "<ul>";

            let suitesTaken = messageData.suites;
            for (var i = 0; i < suitesTaken.length; i++) {
              let found = selectedWordpressSuites.find(
                (element) =>
                  element.suiteIds?.ids &&
                  Array.isArray(element.suiteIds.ids) &&
                  element.suiteIds.ids.includes(Number(suitesTaken[i]))
              );
              if (found) {
                itemTaken += `<li>${found.name} (Suite ID: ${suitesTaken[i]})</li>`;
              } else {
                itemTaken += `<li>Suite ID: ${suitesTaken[i]}</li>`;
              }
            }

            itemTaken += "</ul>";

            Swal.fire({
              icon: "error",
              title: "Error",
              html:
                "<p>The following Properties have already been linked to another city. Please select other Properties.</p>" +
                itemTaken,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: auditResponse.errorMessage.message,
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: auditResponse.errorMessage.message,
          });
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [auditResponse]);

  const [selectedWordpressSuites, setSelectedWordpressSuites] = useState([]);

  const handleWordpressSuitesSelected = ({
    allSelected,
    selectedCount,
    selectedRows,
  }) => {
    setSelectedWordpressSuites(selectedRows);
  };

  const renderContent = () => {
    return (
      <Box
        component="form"
        noValidate
        onSubmit={authState.editAccess && handleSubmit(onSubmit)}
        sx={{ width: "100%" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormPageCard>
              <FormPageHeader
                title={
                  authState.editAccess ? "Edit City" : "View City"
                }
                onBack={goBack}
                subtitle={
                  authState.editAccess
                    ? "Please fill in the required fields."
                    : undefined
                }
              />
              <TextField
                name="name"
                margin="normal"
                fullWidth
                label="Name*"
                variant="outlined"
                inputRef={register}
                error={errors.name != null}
                helperText={errors.name != null ? errors.name.message : null}
                disabled={auditResponse.loading || !authState.editAccess}
              />
              <TextField
                name="name_chinese"
                margin="normal"
                fullWidth
                label="Chinese Name*"
                variant="outlined"
                inputRef={register}
                error={errors.name_chinese != null}
                helperText={
                  errors.name_chinese != null
                    ? errors.name_chinese.message
                    : null
                }
                disabled={auditResponse.loading || !authState.editAccess}
              />
              <TextField
                name="additionalSharerCost"
                margin="normal"
                fullWidth
                label="Additional Sharer Cost (Amount in AUD*)"
                variant="outlined"
                inputRef={register}
                type="number"
                error={errors.additionalSharerCost != null}
                helperText={
                  errors.additionalSharerCost != null
                    ? errors.additionalSharerCost.message
                    : null
                }
                disabled={auditResponse.loading || !authState.editAccess}
              />
            </FormPageCard>
          </Grid>
          <Grid item xs={12}>
            <FormPageCard>
              <Typography component="h2" variant="h6" fontWeight={700}>
                Properties
              </Typography>
              {authState.editAccess && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Please select the Property(s) that belong to this city.
                </Typography>
              )}
              <WordpressSuites
                onSelectedRowsChange={handleWordpressSuitesSelected}
                data={propertiesTable}
                selectedRows={selectedWordpressSuites}
                disabled={auditResponse.loading || !authState.editAccess}
                showSuiteId
              />
              {wordpressSuitesError && (
                <Box sx={errorTextSx}>{wordpressSuitesError}</Box>
              )}
            </FormPageCard>
          </Grid>
          {authState.editAccess && (
            <Grid item xs={12}>
              <FormPageCard>
                <FormActions>
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={auditResponse.loading || !authState.editAccess}
                  >
                    Submit
                  </Button>
                </FormActions>
              </FormPageCard>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <FormPageShell>
      {(propertiesResponse.loading || cityResponse.loading) && (
        <FormPageCard>
          <Loader />
        </FormPageCard>
      )}
      {!propertiesResponse.loading &&
        !cityResponse.loading &&
        renderContent()}
    </FormPageShell>
  );
};

export default EditCity;
